import moment, { Moment } from 'moment';

export const API_DATE_FORMAT = 'YYYY-MM-DD';

export interface QuickDateRange {
  key: string;
  label: string;
  start: string;
  end: string;
  group: 'recent' | 'month' | 'season' | 'year';
}

export const toApiDate = (value: string | Date | Moment): string => moment(value).format(API_DATE_FORMAT);

export const formatDisplayDate = (value: string | Date | Moment): string => moment(value).format('DD MMM YYYY');

export const formatDisplayDateTime = (value: string | Date | Moment): string =>
  moment(value).format('DD MMM YYYY, h:mma');

export const formatDateRangeLabel = (fromDate: string | Date | Moment, toDate: string | Date | Moment): string =>
  `${formatDisplayDate(fromDate)} – ${formatDisplayDate(toDate)}`;

export const getQuickDateRanges = (): QuickDateRange[] => {
  const today = moment().startOf('day');
  const ranges: QuickDateRange[] = [
    {
      key: 'last24Hours',
      label: 'Last 24 hours',
      start: today.clone().subtract(1, 'day').format(API_DATE_FORMAT),
      end: today.format(API_DATE_FORMAT),
      group: 'recent',
    },
    {
      key: 'last7Days',
      label: 'Last 7 days',
      start: today.clone().subtract(7, 'day').format(API_DATE_FORMAT),
      end: today.format(API_DATE_FORMAT),
      group: 'recent',
    },
    {
      key: 'last4Weeks',
      label: 'Last 4 weeks',
      start: today.clone().subtract(28, 'day').format(API_DATE_FORMAT),
      end: today.format(API_DATE_FORMAT),
      group: 'recent',
    },
    {
      key: 'lastMonth',
      label: 'Previous month',
      start: today.clone().subtract(1, 'month').startOf('month').format(API_DATE_FORMAT),
      end: today.clone().startOf('month').subtract(1, 'day').format(API_DATE_FORMAT),
      group: 'month',
    },
    {
      key: 'thisMonth',
      label: 'This month',
      start: today.clone().startOf('month').format(API_DATE_FORMAT),
      end: today.clone().endOf('month').format(API_DATE_FORMAT),
      group: 'month',
    },
  ];

  const seasonStarts: Array<{ key: string; label: string; month: number }> = [
    { key: 'winter', label: 'Winter', month: 5 },
    { key: 'spring', label: 'Spring', month: 8 },
    { key: 'summer', label: 'Summer', month: 11 },
    { key: 'autumn', label: 'Autumn', month: 2 },
  ];

  seasonStarts.forEach((season) => {
    const startMonth = moment().month(season.month).startOf('month');
    const year = startMonth.isAfter(today) ? startMonth.year() - 1 : startMonth.year();
    const start = moment({ year, month: season.month, day: 1 });
    const end = start.clone().add(3, 'months').subtract(1, 'day');

    ranges.push({
      key: season.key,
      label: `${season.label} ${start.year()}`,
      start: start.format(API_DATE_FORMAT),
      end: end.format(API_DATE_FORMAT),
      group: 'season',
    });
  });

  const currentYear = today.year();
  for (let year = currentYear - 3; year <= currentYear; year += 1) {
    ranges.push({
      key: String(year),
      label: String(year),
      start: moment({ year, month: 0, day: 1 }).format(API_DATE_FORMAT),
      end: moment({ year, month: 11, day: 31 }).format(API_DATE_FORMAT),
      group: 'year',
    });
  }

  return ranges;
};

export interface CalendarCell {
  key: string;
  date: string;
  dayNumber: number;
  inMonth: boolean;
  isToday: boolean;
}

export const getCalendarMonth = (month: Moment, fromDate: string | Date, toDate: string | Date) => {
  const monthStart = month.clone().startOf('month');
  const gridStart = monthStart.clone().startOf('isoWeek');
  const from = fromDate ? moment(fromDate).startOf('day') : null;
  const to = toDate ? moment(toDate).startOf('day') : null;
  const today = moment().startOf('day');

  const cells: CalendarCell[] = [];
  for (let index = 0; index < 42; index += 1) {
    const day = gridStart.clone().add(index, 'day');
    const date = day.format(API_DATE_FORMAT);
    cells.push({
      key: date,
      date,
      dayNumber: day.date(),
      inMonth: day.month() === monthStart.month(),
      isToday: day.isSame(today, 'day'),
    });
  }

  return {
    monthLabel: monthStart.format('MMMM YYYY'),
    monthStart,
    cells,
    isInRange: (date: string): boolean =>
      !!from && !!to && moment(date, API_DATE_FORMAT, true).isBetween(from, to, 'day', '[]'),
    isRangeStart: (date: string): boolean => !!from && date === from.format(API_DATE_FORMAT),
    isRangeEnd: (date: string): boolean => !!to && date === to.format(API_DATE_FORMAT),
  };
};

export const clampDateRange = (start: string | Date, end: string | Date): { fromDate: string; toDate: string } => {
  const startDate = start ? moment(start) : moment.invalid();
  const endDate = end ? moment(end) : moment.invalid();

  if (!startDate.isValid() || !endDate.isValid()) {
    return { fromDate: start ? toApiDate(start) : '', toDate: end ? toApiDate(end) : '' };
  }

  if (startDate.isAfter(endDate)) {
    return { fromDate: endDate.format(API_DATE_FORMAT), toDate: startDate.format(API_DATE_FORMAT) };
  }

  return { fromDate: startDate.format(API_DATE_FORMAT), toDate: endDate.format(API_DATE_FORMAT) };
};
